import { API_BASE_URL } from '../utils/constants';

export interface EventStreamUpdate {
  type: 'connected' | 'event_created' | 'event_updated' | 'event_deleted' | 'capacity_changed';
  eventId?: string;
  data?: any;
  timestamp: string;
}

type EventStreamCallback = (update: EventStreamUpdate) => void;

class EventStreamService {
  private eventSource: EventSource | null = null;
  private callbacks: Set<EventStreamCallback> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // More attempts before giving up
  private reconnectDelay = 500; // Faster initial reconnect (500ms base)
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    if (this.eventSource) {
      return; // Already connected
    }

    try {
      this.eventSource = new EventSource(`${API_BASE_URL}/sse/events`);

      this.eventSource.onopen = () => {
        console.log('SSE connected');
        this.reconnectAttempts = 0;
      };

      this.eventSource.onmessage = (event) => {
        try {
          const update: EventStreamUpdate = JSON.parse(event.data);
          this.callbacks.forEach((callback) => callback(update));
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      };

      this.eventSource.onerror = () => {
        console.warn('SSE connection error');
        this.disconnect();
        this.attemptReconnect();
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
      this.attemptReconnect();
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('Max SSE reconnect attempts reached, falling back to polling');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`Attempting SSE reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Force a reconnection attempt. Resets reconnect counter and tries again.
   * Called by polling fallback to periodically retry SSE connection.
   */
  tryReconnect(): void {
    if (this.isConnected()) {
      return; // Already connected
    }
    // Reset attempts and try fresh connection
    this.reconnectAttempts = 0;
    this.disconnect();
    if (this.callbacks.size > 0) {
      this.connect();
    }
  }

  subscribe(callback: EventStreamCallback): () => void {
    this.callbacks.add(callback);

    // Connect if this is the first subscriber
    if (this.callbacks.size === 1) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);

      // Disconnect if no more subscribers
      if (this.callbacks.size === 0) {
        this.disconnect();
      }
    };
  }

  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }
}

export const eventStreamService = new EventStreamService();
